/**
 * StreetDiceReportCard — compact revenue tile for the Reports tab. Shows
 * today + lifetime house take from Street Dice, split into come-out 2/12
 * rake and bot winnings routed. Deep-link into the Dice → Revenue admin tab
 * for the full breakdown. Full tile lives at admin › Dice › Revenue.
 */
import { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { Dice5, Bot, Landmark, ExternalLink } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const fmt$ = (n) =>
    `$${Number(n || 0).toLocaleString(undefined, {
        minimumFractionDigits: 2, maximumFractionDigits: 2,
    })}`;

const StreetDiceReportCard = () => {
    const [data, setData] = useState(null);
    const [error, setError] = useState(null);

    const load = useCallback(async () => {
        try {
            const { data } = await axios.get(
                `${API}/admin/street-dice/rake-summary`,
                { withCredentials: true },
            );
            setData(data);
            setError(null);
        } catch (err) {
            setError(err?.response?.data?.detail || 'Could not load');
        }
    }, []);

    useEffect(() => {
        load();
        const id = setInterval(load, 60_000);
        return () => clearInterval(id);
    }, [load]);

    const today = data?.breakdown?.today || {};
    const lifetime = data?.breakdown?.lifetime || {};
    const todayRake = today.come_out_rake || { amount: 0, events: 0 };
    const todayBots = today.bot_winnings || { amount: 0, events: 0 };
    const lifeRake = lifetime.come_out_rake || { amount: 0, events: 0 };
    const lifeBots = lifetime.bot_winnings || { amount: 0, events: 0 };
    const todayTotal = todayRake.amount + todayBots.amount;
    const lifeTotal = data?.lifetime ?? (lifeRake.amount + lifeBots.amount);

    return (
        <div
            className="rounded-2xl border p-4"
            style={{
                background: 'linear-gradient(180deg, rgba(255,215,0,0.06) 0%, rgba(255,215,0,0.02) 100%)',
                borderColor: 'rgba(255,215,0,0.25)',
            }}
            data-testid="reports-street-dice-card"
        >
            <div className="flex items-center gap-2 mb-3">
                <Landmark className="w-4 h-4 text-[#FFD700]" />
                <p className="text-xs uppercase tracking-wider text-[#A0AAB2] font-bold">
                    Street Dice · House Revenue
                </p>
                <a
                    href="#dice-revenue"
                    onClick={(e) => {
                        e.preventDefault();
                        window.dispatchEvent(new CustomEvent('betohalics:admin-navigate', {
                            detail: { section: 'dice', tab: 'dice-revenue' },
                        }));
                    }}
                    className="ml-auto inline-flex items-center gap-1 text-[10px] font-bold text-[#FFD700] hover:underline"
                    data-testid="reports-street-dice-open-tab"
                >
                    Open Dice tab <ExternalLink className="w-3 h-3" />
                </a>
            </div>

            {error ? (
                <p className="text-xs text-[#FF3B30]">{error}</p>
            ) : (
                <>
                    <div className="grid grid-cols-2 gap-3 mb-3">
                        <div>
                            <p className="text-[10px] uppercase tracking-wider text-[#A0AAB2]">Today (CT)</p>
                            <p className="text-2xl font-black text-white tabular-nums" data-testid="reports-street-dice-today">
                                {fmt$(todayTotal)}
                            </p>
                        </div>
                        <div>
                            <p className="text-[10px] uppercase tracking-wider text-[#A0AAB2]">Lifetime</p>
                            <p className="text-2xl font-black text-[#FFD700] tabular-nums" data-testid="reports-street-dice-lifetime">
                                {fmt$(lifeTotal)}
                            </p>
                        </div>
                    </div>
                    <div className="grid grid-cols-2 gap-2 text-[11px]">
                        <div className="rounded-lg border border-white/10 bg-black/20 px-2 py-1.5">
                            <div className="flex items-center gap-1.5">
                                <Dice5 className="w-3 h-3 text-[#FFD700]" />
                                <span className="text-white/70">2/12 rake</span>
                            </div>
                            <p className="text-sm font-black text-white tabular-nums mt-0.5">
                                {fmt$(todayRake.amount)} <span className="text-white/40 text-[10px]">today</span>
                            </p>
                            <p className="text-[10px] text-white/40">life {fmt$(lifeRake.amount)}</p>
                        </div>
                        <div className="rounded-lg border border-white/10 bg-black/20 px-2 py-1.5">
                            <div className="flex items-center gap-1.5">
                                <Bot className="w-3 h-3 text-[#22c55e]" />
                                <span className="text-white/70">Bot winnings</span>
                            </div>
                            <p className="text-sm font-black text-white tabular-nums mt-0.5">
                                {fmt$(todayBots.amount)} <span className="text-white/40 text-[10px]">today</span>
                            </p>
                            <p className="text-[10px] text-white/40">life {fmt$(lifeBots.amount)}</p>
                        </div>
                    </div>
                </>
            )}
        </div>
    );
};

export default StreetDiceReportCard;
